import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError, timer } from "rxjs";
import { delay, switchMap, takeWhile } from "rxjs/operators";
import { environment } from "src/environments/environment";

/** Réseaux mobile money pris en charge (MGA via passerelle maison côté Nest). */
export type UssdMobileMoneyOperator = "mvola" | "orange_money" | "airtel_money";

export type UssdPaymentStatus = "PENDING" | "PAID" | "FAILED" | "EXPIRED";

export interface UssdPaymentCreateBody {
    amountMga: number;
    currency: "MGA";
    operator: UssdMobileMoneyOperator;
    /** Numéro du compte mobile money (chiffres, ex. 034… ou 261…). */
    payerPhone: string;
    description: string;
    /** ex. planId, user email renvoyé tel quel par l’API pour corrélation. */
    metadata?: Record<string, string | number | boolean | null>;
}

export interface UssdPaymentInstruction {
    /** Référence commande : chiffres uniquement, 6 caractères max (contrat Nest / SMS). */
    referenceId: string;
    amountMga: number;
    currency: "MGA";
    operator: UssdMobileMoneyOperator;
    /** Code à composer sur le téléphone (USSD). */
    ussdDialString: string;
    expiresAtIso: string;
    /** Texte d’aide affiché côté UI. */
    customerHint: string;
}

export interface UssdPaymentStatusPayload {
    /** Même format que la création : uniquement des chiffres. */
    referenceId: string;
    status: UssdPaymentStatus;
    amountMga?: number;
    paidAtIso?: string;
    failureReason?: string;
}

/** Fenêtre pendant laquelle le mock accepte encore un passage à PAID ; après → EXPIRED. */
const MOCK_PAYMENT_VALIDITY_MS = 5 * 60 * 1000;

interface MockPaymentState {
    status: UssdPaymentStatus;
    pollCount: number;
    amountMga: number;
    createdAtMs: number;
}

@Injectable({
    providedIn: "root",
})
export class UssdMobileMoneyPaymentService {
    private readonly mockStore = new Map<string, MockPaymentState>();

    constructor(private readonly http: HttpClient) {}

    createPaymentRequest(body: UssdPaymentCreateBody): Observable<UssdPaymentInstruction> {
        if (environment.useMockPayments) {
            return this.mockCreate$(body);
        }
        const url = `${environment.apiUrl}/payments/ussd-mobile-money/request`;
        return this.http.post<UssdPaymentInstruction>(url, body);
    }

    getPaymentStatus(referenceId: string): Observable<UssdPaymentStatusPayload> {
        if (environment.useMockPayments) {
            return this.mockStatus$(referenceId);
        }
        const url = `${environment.apiUrl}/payments/ussd-mobile-money/${encodeURIComponent(referenceId)}/status`;
        return this.http.get<UssdPaymentStatusPayload>(url);
    }

    /**
     * Poll jusqu’à statut terminal (PAID / FAILED / EXPIRED) ou annulation via unsubscribe.
     */
    pollUntilTerminal(referenceId: string, intervalMs = 2800): Observable<UssdPaymentStatusPayload> {
        return timer(0, intervalMs).pipe(
            switchMap(() => this.getPaymentStatus(referenceId)),
            takeWhile((s) => !this.isTerminal(s.status), true),
        );
    }

    private isTerminal(status: UssdPaymentStatus): boolean {
        return status === "PAID" || status === "FAILED" || status === "EXPIRED";
    }

    private mockCreate$(body: UssdPaymentCreateBody): Observable<UssdPaymentInstruction> {
        if (body.amountMga <= 0 || !Number.isFinite(body.amountMga)) {
            return throwError(() => new Error("Montant MGA invalide pour un paiement mobile."));
        }
        const phoneDigits = body.payerPhone.replace(/\D/g, "");
        if (phoneDigits.length < 9) {
            return throwError(() => new Error("Indiquez un numéro de téléphone valide (au moins 9 chiffres)."));
        }
        const referenceId = this.nextNumericReferenceId();
        const amountMga = Math.round(body.amountMga);
        const createdAtMs = Date.now();
        this.mockStore.set(referenceId, { status: "PENDING", pollCount: 0, amountMga, createdAtMs });

        const ussdDialString = this.mockUssdDial(body.operator, amountMga, referenceId);
        const expiresAt = new Date(createdAtMs + MOCK_PAYMENT_VALIDITY_MS).toISOString();

        const instruction: UssdPaymentInstruction = {
            referenceId,
            amountMga,
            currency: "MGA",
            operator: body.operator,
            ussdDialString,
            expiresAtIso: expiresAt,
            customerHint:
                "Composez le code USSD sur le numéro lié à votre compte mobile money. " +
                "Cette demande est valable 5 minutes ; sans confirmation dans ce délai elle sera marquée expirée. " +
                "Le statut est vérifié automatiquement par notre système.",
        };

        return of(instruction).pipe(delay(450));
    }

    private mockStatus$(referenceId: string): Observable<UssdPaymentStatusPayload> {
        const state = this.mockStore.get(referenceId);
        if (!state) {
            return throwError(() => new Error("Référence de paiement inconnue (mock)."));
        }

        const elapsed = Date.now() - state.createdAtMs;

        if (state.status === "PENDING") {
            if (elapsed >= MOCK_PAYMENT_VALIDITY_MS) {
                state.status = "EXPIRED";
            } else {
                state.pollCount += 1;
                if (state.pollCount >= 3) {
                    state.status = "PAID";
                }
            }
        }

        const payload: UssdPaymentStatusPayload = {
            referenceId,
            status: state.status,
            amountMga: state.amountMga,
            ...(state.status === "PAID" ? { paidAtIso: new Date().toISOString() } : {}),
            ...(state.status === "EXPIRED"
                ? { failureReason: "Délai de 5 minutes dépassé sans confirmation de paiement." }
                : {}),
        };
        return of(payload).pipe(delay(220));
    }

    /** Référence 100 % numérique, 6 chiffres max (SMS / affichage). */
    private nextNumericReferenceId(): string {
        for (let attempt = 0; attempt < 20; attempt += 1) {
            const id = Math.floor(Math.random() * 1_000_000)
                .toString()
                .padStart(6, "0");
            if (!this.mockStore.has(id)) {
                return id;
            }
        }
        return (Date.now() % 1_000_000).toString().padStart(6, "0");
    }

    private mockUssdDial(operator: UssdMobileMoneyOperator, amountMga: number, ref: string): string {
        const amt = String(Math.max(0, Math.round(amountMga)));
        const numericRef = ref.replace(/\D/g, "").slice(0, 6);
        switch (operator) {
            case "mvola":
                return `#111*1*2*0342390673*${amt}*${numericRef}#`;
            case "orange_money":
                return `*144*1*${amt}*${numericRef}#`;
            case "airtel_money":
                return `*150*1*${amt}*${numericRef}#`;
            default:
                return `*000*${amt}*${numericRef}#`;
        }
    }
}
